/**
 * Framework-agnostic event decoders for confidential token contracts.
 * Works with raw log data from any provider.
 */

import type { EncryptedValue } from "../relayer/relayer-sdk.types";
import { getAddress, keccak256, toBytes, type Address, type Hex } from "viem";
import { prefixHex } from "../utils";
import type { RawLog } from "../types/transaction";
export type { RawLog } from "../types/transaction";

function eventTopic(signature: string): Hex {
  return keccak256(toBytes(signature));
}

// ---------------------------------------------------------------------------
// Event topic0 constants (keccak256 of canonical signature)
// ---------------------------------------------------------------------------

/**
 * Event topic0 constants (keccak256 of the canonical Solidity signature).
 * Pass to `getLogs({ topics: [Object.values(Topics)] })` to fetch all events.
 */
export const Topics = {
  /** `ConfidentialTransfer(address indexed from, address indexed to, bytes32 indexed amount)` */
  ConfidentialTransfer: eventTopic("ConfidentialTransfer(address,address,bytes32)"),
  /** `Wrap(address indexed to, uint256 roundedAmount, euint64 encryptedWrappedAmount)` */
  Wrap: eventTopic("Wrap(address,uint256,bytes32)"),
  /** `UnwrapRequested(address indexed receiver, bytes32 indexed unwrapRequestId, bytes32 amount)` */
  UnwrapRequested: eventTopic("UnwrapRequested(address,bytes32,bytes32)"),
  /** `UnwrapFinalized(address indexed receiver, bytes32 indexed unwrapRequestId, bytes32 encryptedAmount, uint64 cleartextAmount)` */
  UnwrapFinalized: eventTopic("UnwrapFinalized(address,bytes32,bytes32,uint64)"),
} as const;

// ---------------------------------------------------------------------------
// Typed event interfaces
// ---------------------------------------------------------------------------

/** Decoded `ConfidentialTransfer` event — an encrypted token transfer. */
export interface ConfidentialTransferEvent {
  readonly eventName: "ConfidentialTransfer";
  /** Sender address. */
  readonly from: Address;
  /** Receiver address. */
  readonly to: Address;
  /** FHE encrypted value for the transferred amount. */
  readonly encryptedAmount: EncryptedValue;
}

/**
 * Decoded `Wrap` event — an ERC-20 shield (wrap) operation. Emitted alongside a
 * `ConfidentialTransfer(from=zeroAddress, ...)`; `encryptedWrappedAmount` is the same FHE
 * handle that lands in `ConfidentialTransfer.encryptedAmount`.
 */
export interface WrapEvent {
  readonly eventName: "Wrap";
  /** Receiver of the minted confidential tokens. */
  readonly to: Address;
  /** Cleartext underlying ERC-20 tokens deposited, rounded down to a multiple of the rate. */
  readonly roundedAmount: bigint;
  /** FHE encrypted value for the wrapped (minted) amount. */
  readonly encryptedWrappedAmount: EncryptedValue;
}

/** Decoded `UnwrapRequested` event — an unshield request submitted. */
export interface UnwrapRequestedEvent {
  readonly eventName: "UnwrapRequested";
  /** Address that will receive the unwrapped ERC-20 tokens. */
  readonly receiver: Address;
  /** FHE encrypted value for the requested unshield amount. */
  readonly encryptedAmount: EncryptedValue;
  /** Request identifier from the `UnwrapRequested` event topic. */
  readonly unwrapRequestId?: EncryptedValue;
}

/** Decoded `UnwrapFinalized` event — an unshield completed on-chain. */
export interface UnwrapFinalizedEvent {
  readonly eventName: "UnwrapFinalized";
  /** Address receiving the unwrapped ERC-20 tokens. */
  readonly receiver: Address;
  /** FHE encrypted value of the burnt confidential balance. */
  readonly encryptedAmount: EncryptedValue;
  /** Cleartext amount of underlying ERC-20 tokens returned. */
  readonly cleartextAmount: bigint;
  /** Request identifier from the `UnwrapFinalized` event topic. */
  readonly unwrapRequestId?: EncryptedValue;
}

/** Union of all decoded confidential token event types. */
export type OnChainEvent =
  | ConfidentialTransferEvent
  | WrapEvent
  | UnwrapRequestedEvent
  | UnwrapFinalizedEvent;

// ---------------------------------------------------------------------------
// ABI decoding helpers (no external deps)
// ---------------------------------------------------------------------------

function topicToAddress(topic: Hex): Address {
  return getAddress(prefixHex(topic.slice(-40)));
}

function topicToBytes32(topic: Hex): EncryptedValue {
  // EVM topics are already 32-byte 0x-prefixed hex — cast directly
  return topic as EncryptedValue;
}

function wordAt(data: Hex, index: number): string {
  // data starts with "0x", each word is 64 hex chars (32 bytes)
  const start = 2 + index * 64;
  const word = data.slice(start, start + 64);
  return word.length === 64 ? word : word.padEnd(64, "0");
}

function wordToAddress(data: Hex, index: number): Address {
  return getAddress(prefixHex(wordAt(data, index).slice(-40)));
}

function wordToBigInt(data: Hex, index: number): bigint {
  return BigInt("0x" + wordAt(data, index));
}

function wordToBytes32(data: Hex, index: number): EncryptedValue {
  // wordAt returns exactly 64 hex chars — prefix and cast directly
  return prefixHex(wordAt(data, index)) as EncryptedValue;
}

// ---------------------------------------------------------------------------
// Individual decoders
// ---------------------------------------------------------------------------

/**
 * ConfidentialTransfer(address indexed from, address indexed to, bytes32 indexed amount)
 * All 3 params indexed → topics[1..3], no data.
 */
export function decodeConfidentialTransfer(log: RawLog): ConfidentialTransferEvent | null {
  if (log.topics[0] !== Topics.ConfidentialTransfer) {
    return null;
  }
  if (log.topics.length < 4) {
    return null;
  }

  return {
    eventName: "ConfidentialTransfer",
    from: topicToAddress(log.topics[1]!),
    to: topicToAddress(log.topics[2]!),
    encryptedAmount: topicToBytes32(log.topics[3]!),
  };
}

/**
 * Wrap(address indexed to, uint256 roundedAmount, euint64 encryptedWrappedAmount)
 * Indexed: to (topics[1])
 * Data: roundedAmount (uint256), encryptedWrappedAmount (bytes32)
 */
export function decodeWrap(log: RawLog): WrapEvent | null {
  if (log.topics[0] !== Topics.Wrap) {
    return null;
  }
  if (log.topics.length < 2) {
    return null;
  }

  return {
    eventName: "Wrap",
    to: topicToAddress(log.topics[1]!),
    roundedAmount: wordToBigInt(log.data, 0),
    encryptedWrappedAmount: wordToBytes32(log.data, 1),
  };
}

/**
 * UnwrapRequested(address indexed receiver, bytes32 indexed unwrapRequestId, bytes32 amount)
 */
export function decodeUnwrapRequested(log: RawLog): UnwrapRequestedEvent | null {
  if (log.topics[0] !== Topics.UnwrapRequested) {
    return null;
  }
  if (log.topics.length < 3) {
    return null;
  }

  return {
    eventName: "UnwrapRequested",
    receiver: topicToAddress(log.topics[1]!),
    unwrapRequestId: topicToBytes32(log.topics[2]!),
    encryptedAmount: wordToBytes32(log.data, 0),
  };
}

/**
 * UnwrapFinalized(address indexed receiver, bytes32 indexed unwrapRequestId, bytes32 encryptedAmount, uint64 cleartextAmount)
 */
export function decodeUnwrapFinalized(log: RawLog): UnwrapFinalizedEvent | null {
  if (log.topics[0] !== Topics.UnwrapFinalized) {
    return null;
  }
  if (log.topics.length < 3) {
    return null;
  }

  return {
    eventName: "UnwrapFinalized",
    receiver: topicToAddress(log.topics[1]!),
    unwrapRequestId: topicToBytes32(log.topics[2]!),
    encryptedAmount: wordToBytes32(log.data, 0),
    cleartextAmount: wordToBigInt(log.data, 1),
  };
}

// ---------------------------------------------------------------------------
// Convenience helpers
// ---------------------------------------------------------------------------

/**
 * Try all decoders on a single log and return the first match, or `null`.
 *
 * @example
 * ```ts
 * const event = decodeOnChainEvent(log);
 * if (event?.eventName === "ConfidentialTransfer") {
 *   console.log(event.from, event.to);
 * }
 * ```
 */
export function decodeOnChainEvent(log: RawLog): OnChainEvent | null {
  return (
    decodeConfidentialTransfer(log) ??
    decodeWrap(log) ??
    decodeUnwrapRequested(log) ??
    decodeUnwrapFinalized(log)
  );
}

/**
 * Batch-decode an array of logs, skipping unrecognized entries.
 *
 * @example
 * ```ts
 * const events = decodeOnChainEvents(receipt.logs);
 * ```
 */
export function decodeOnChainEvents(logs: readonly RawLog[]): OnChainEvent[] {
  const events: OnChainEvent[] = [];
  for (const log of logs) {
    const event = decodeOnChainEvent(log);
    if (event) {
      events.push(event);
    }
  }
  return events;
}

/**
 * Find the first {@link UnwrapRequestedEvent} in a logs array.
 *
 * @example
 * ```ts
 * const event = findUnwrapRequested(receipt.logs);
 * if (event) console.log(event.receiver, event.encryptedAmount);
 * ```
 */
export function findUnwrapRequested(logs: readonly RawLog[]): UnwrapRequestedEvent | null {
  for (const log of logs) {
    const event = decodeUnwrapRequested(log);
    if (event) {
      return event;
    }
  }
  return null;
}

/**
 * Find the first {@link WrapEvent} in a logs array.
 *
 * @example
 * ```ts
 * const event = findWrap(receipt.logs);
 * if (event) console.log(event.to, event.roundedAmount);
 * ```
 */
export function findWrap(logs: readonly RawLog[]): WrapEvent | null {
  for (const log of logs) {
    const event = decodeWrap(log);
    if (event) {
      return event;
    }
  }
  return null;
}

/**
 * All confidential token event topic0 hashes.
 * Pass to `getLogs({ topics: [TOKEN_TOPICS] })` to fetch
 * all confidential token events in a single RPC call.
 */
export const TOKEN_TOPICS = [
  Topics.ConfidentialTransfer,
  Topics.Wrap,
  Topics.UnwrapRequested,
  Topics.UnwrapFinalized,
] as const;

// ---------------------------------------------------------------------------
// ACL delegation event topic0 constants
// ---------------------------------------------------------------------------

/**
 * ACL delegation event topic0 constants (keccak256 of the canonical Solidity signature).
 * These are ACL events, NOT token events — they are emitted by the ACL contract.
 */
export const AclTopics = {
  /** `DelegatedForUserDecryption(address indexed delegator, address indexed delegate, address contractAddress, uint64 delegationCounter, uint64 oldExpirationDate, uint64 newExpirationDate)` */
  DelegatedForUserDecryption: eventTopic(
    "DelegatedForUserDecryption(address,address,address,uint64,uint64,uint64)",
  ),
  /** `RevokedDelegationForUserDecryption(address indexed delegator, address indexed delegate, address contractAddress, uint64 delegationCounter, uint64 oldExpirationDate)` */
  RevokedDelegationForUserDecryption: eventTopic(
    "RevokedDelegationForUserDecryption(address,address,address,uint64,uint64)",
  ),
} as const;

// ---------------------------------------------------------------------------
// ACL delegation event interfaces
// ---------------------------------------------------------------------------

/** Decoded `DelegatedForUserDecryption` event — a delegation was created or renewed. */
export interface DelegatedForUserDecryptionEvent {
  readonly eventName: "DelegatedForUserDecryption";
  /** Address of the delegator (the account granting access). */
  readonly delegator: Address;
  /** Address of the delegate (the account receiving access). */
  readonly delegate: Address;
  /** Contract address the delegation applies to. */
  readonly contractAddress: Address;
  /** Monotonic delegation counter. */
  readonly delegationCounter: bigint;
  /** Previous expiration timestamp (0 if first delegation). */
  readonly oldExpirationDate: bigint;
  /** New expiration timestamp. */
  readonly newExpirationDate: bigint;
}

/** Decoded `RevokedDelegationForUserDecryption` event — a delegation was revoked. */
export interface RevokedDelegationForUserDecryptionEvent {
  readonly eventName: "RevokedDelegationForUserDecryption";
  /** Address of the delegator. */
  readonly delegator: Address;
  /** Address of the delegate. */
  readonly delegate: Address;
  /** Contract address the revocation applies to. */
  readonly contractAddress: Address;
  /** Monotonic delegation counter. */
  readonly delegationCounter: bigint;
  /** Expiration date that was active before revocation. */
  readonly oldExpirationDate: bigint;
}

/** Union of all decoded ACL delegation event types. */
export type AclEvent = DelegatedForUserDecryptionEvent | RevokedDelegationForUserDecryptionEvent;

// ---------------------------------------------------------------------------
// ACL delegation event decoders
// ---------------------------------------------------------------------------

/**
 * DelegatedForUserDecryption(address indexed delegator, address indexed delegate,
 *   address contractAddress, uint64 delegationCounter, uint64 oldExpirationDate, uint64 newExpirationDate)
 * Indexed: delegator (topics[1]), delegate (topics[2])
 * Data: contractAddress, delegationCounter, oldExpirationDate, newExpirationDate
 */
export function decodeDelegatedForUserDecryption(
  log: RawLog,
): DelegatedForUserDecryptionEvent | null {
  if (log.topics[0] !== AclTopics.DelegatedForUserDecryption) {
    return null;
  }
  if (log.topics.length < 3) {
    return null;
  }

  return {
    eventName: "DelegatedForUserDecryption",
    delegator: topicToAddress(log.topics[1]!),
    delegate: topicToAddress(log.topics[2]!),
    contractAddress: wordToAddress(log.data, 0),
    delegationCounter: wordToBigInt(log.data, 1),
    oldExpirationDate: wordToBigInt(log.data, 2),
    newExpirationDate: wordToBigInt(log.data, 3),
  };
}

/**
 * RevokedDelegationForUserDecryption(address indexed delegator, address indexed delegate,
 *   address contractAddress, uint64 delegationCounter, uint64 oldExpirationDate)
 * Indexed: delegator (topics[1]), delegate (topics[2])
 * Data: contractAddress, delegationCounter, oldExpirationDate
 */
export function decodeRevokedDelegationForUserDecryption(
  log: RawLog,
): RevokedDelegationForUserDecryptionEvent | null {
  if (log.topics[0] !== AclTopics.RevokedDelegationForUserDecryption) {
    return null;
  }
  if (log.topics.length < 3) {
    return null;
  }

  return {
    eventName: "RevokedDelegationForUserDecryption",
    delegator: topicToAddress(log.topics[1]!),
    delegate: topicToAddress(log.topics[2]!),
    contractAddress: wordToAddress(log.data, 0),
    delegationCounter: wordToBigInt(log.data, 1),
    oldExpirationDate: wordToBigInt(log.data, 2),
  };
}

/**
 * Try all ACL delegation decoders on a single log and return the first match, or `null`.
 */
export function decodeAclEvent(log: RawLog): AclEvent | null {
  return decodeDelegatedForUserDecryption(log) ?? decodeRevokedDelegationForUserDecryption(log);
}

/**
 * Batch-decode an array of logs for ACL delegation events, skipping unrecognized entries.
 */
export function decodeAclEvents(logs: readonly RawLog[]): AclEvent[] {
  const events: AclEvent[] = [];
  for (const log of logs) {
    const event = decodeAclEvent(log);
    if (event) {
      events.push(event);
    }
  }
  return events;
}

/**
 * Find the first {@link DelegatedForUserDecryptionEvent} in a logs array.
 */
export function findDelegatedForUserDecryption(
  logs: readonly RawLog[],
): DelegatedForUserDecryptionEvent | null {
  for (const log of logs) {
    const event = decodeDelegatedForUserDecryption(log);
    if (event) {
      return event;
    }
  }
  return null;
}

/**
 * Find the first {@link RevokedDelegationForUserDecryptionEvent} in a logs array.
 */
export function findRevokedDelegationForUserDecryption(
  logs: readonly RawLog[],
): RevokedDelegationForUserDecryptionEvent | null {
  for (const log of logs) {
    const event = decodeRevokedDelegationForUserDecryption(log);
    if (event) {
      return event;
    }
  }
  return null;
}

/**
 * Both ACL delegation event topic0 hashes.
 * Pass to `getLogs({ topics: [ACL_TOPICS] })` to fetch
 * all delegation events in a single RPC call.
 */
export const ACL_TOPICS = [
  AclTopics.DelegatedForUserDecryption,
  AclTopics.RevokedDelegationForUserDecryption,
] as const;
